import { Injectable } from '@angular/core';
import { StorageService, TripsService } from '../services';
import { ITrip, IBase } from '../models';
import { objectToCamel } from 'ts-case-convert';
import { initialPaginationState } from '../store/initialisation/pagination';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { initialTripsState } from '../store/initialisation/trips';
import { ExpeditionStatus, ExpeditionType } from '../models/enums';
import {
  DEFAULT_TRIP_STATUS,
  ITripsFilters,
  deserializeTripsFilters,
  parseTripsFiltersFromStorage,
} from '../filters';

@Injectable({
  providedIn: 'root',
})
export class TripsResolver {
  // Tracks the latest list-fetch request; stale completions are dropped so a
  // page change isn't overwritten by a fingerprint-dedup'd earlier call.
  private listGeneration = 0;
  // Fingerprint of the last successful fetch. Skips redundant resolver runs on
  // sibling-route navigation (/trips -> /trips/:id) so the list doesn't refetch
  // and lose the current page.
  private lastFetchFingerprint: string | null = null;
  // Fingerprint without page. Lets us tell a pure page change from a filter
  // change so the pagination only collapses to "currentPage ···" when totals
  // are genuinely unknown.
  private lastFilterFingerprint: string | null = null;

  constructor(
    private tripsService: TripsService,
    public storageService: StorageService,
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async resolve(route: ActivatedRouteSnapshot, state?: RouterStateSnapshot) {
    this.storageService.setRouteName('trips')
    const size = route.data['trips']?.pagination.number_of_elements || 8;
    const search = route.queryParamMap.get('search') ?? undefined;
    const filters = await this.resolveFilters(route);

    const filterFingerprint = JSON.stringify({
      size, search: search ?? null,
      clients: filters.selectedClients.map((c) => c.id).sort(),
      operators: filters.selectedOperators.map((o) => o.id).sort(),
      towns: filters.selectedTowns.map((t) => t.id).sort(),
      services: filters.selectedServices.map((s) => s.id).sort(),
      routes: filters.selectedRoutes.map((r) => r.id).sort(),
      types: [...filters.selectedTypes].sort(),
      status: [...filters.selectedStatus].sort(),
      availability: filters.selectedAvailabilityId,
    });
    // A genuine search/filter change explicitly clears `page` from the URL to reset
    // pagination — don't let that be confused with the sibling-route case below, where
    // `page` is also absent from the child route's params but the current page should stick.
    const filtersChanged = filterFingerprint !== this.lastFilterFingerprint;
    const page = this.resolvePage(route, filtersChanged);

    const fingerprint = JSON.stringify({
      size, page, search: search ?? null,
      clients: filters.selectedClients.map((c) => c.id).sort(),
      operators: filters.selectedOperators.map((o) => o.id).sort(),
      towns: filters.selectedTowns.map((t) => t.id).sort(),
      services: filters.selectedServices.map((s) => s.id).sort(),
      routes: filters.selectedRoutes.map((r) => r.id).sort(),
      types: [...filters.selectedTypes].sort(),
      status: [...filters.selectedStatus].sort(),
      availability: filters.selectedAvailabilityId,
    });
    if (fingerprint === this.lastFetchFingerprint && initialTripsState.value?.length) {
      return;
    }

    initialTripsState.isFetchingTrips = true;
    initialTripsState.value = [];
    if (filtersChanged) {
      initialTripsState.pagination.totalPages = 0;
      initialTripsState.pagination.totalElements = 0;
    }
    this.lastFilterFingerprint = filterFingerprint;

    this.resolveAsync(size, page, filters, search, fingerprint);
  }

  private resolvePage(route: ActivatedRouteSnapshot, forceFirstPage = false): number {
    const pageParam = route.queryParamMap.get('page');
    const pageFromUrl = pageParam ? Number(pageParam) : null;
    if (pageFromUrl && !isNaN(pageFromUrl) && pageFromUrl > 0) return pageFromUrl;
    if (forceFirstPage) return 1;
    return route.data['trips']?.pagination.actualPage || initialTripsState.pagination.actualPage || 1;
  }

  private async resolveFilters(route: ActivatedRouteSnapshot): Promise<ITripsFilters> {
    // URL is the source of truth whenever there is *any* query param. Storage is only
    // consulted on a truly bare URL (the "sidebar tab-switch" entry path).
    if (route.queryParamMap.keys.length > 0) {
      return deserializeTripsFilters(route.queryParamMap);
    }
    const stored = parseTripsFiltersFromStorage(await this.storageService.getTripsFilters());
    const areaJson = await this.storageService.getArea();
    const selectedTowns: IBase[] = areaJson ? JSON.parse(areaJson) : [];
    if (stored) {
      return {
        ...stored,
        selectedTowns,
        selectedStatus: stored.selectedStatus?.length ? stored.selectedStatus : DEFAULT_TRIP_STATUS,
      };
    }
    return {
      selectedClients: [],
      selectedOperators: [],
      selectedTowns,
      selectedServices: [],
      selectedRoutes: [],
      selectedTypes: [],
      selectedStatus: DEFAULT_TRIP_STATUS,
      selectedAvailabilityId: null,
    };
  }

  async resolveAsync(size: number, page: number, filters: ITripsFilters, search?: string, fingerprint?: string) {
    const myGen = ++this.listGeneration;
    const {
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
    } = await this.tripsService.getTripsFiltered(
      size, page,
      filters.selectedClients, filters.selectedOperators, filters.selectedTowns,
      filters.selectedTypes, filters.selectedStatus, search,
      filters.selectedServices, filters.selectedRoutes, undefined,
      filters.selectedAvailabilityId
    );
    // Drop the response if a newer resolver call has started in the meantime.
    if (myGen !== this.listGeneration) return;
    const result = this.getData(content, number_of_elements, total_elements, total_pages, first, last, page);
    if (fingerprint !== undefined) this.lastFetchFingerprint = fingerprint;
    return result;
  }

  async getTrips(
    route: ActivatedRouteSnapshot,
    expeditionClients: IBase[],
    expeditionOperators: IBase[],
    expeditionTowns: IBase[],
    expeditionTypes: ExpeditionType[],
    expeditionStatus: ExpeditionStatus[],
    searchValue?: string,
    expeditionServices?: IBase[],
    expeditionRoutes?: IBase[],
    availabilityId?: number | null
  ) {
    const page = this.resolvePage(route);
    const size = route.data['trips']?.pagination.number_of_elements || 8;
    const {
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
    } = await this.tripsService.getTripsFiltered(size, page, expeditionClients, expeditionOperators, expeditionTowns, expeditionTypes, expeditionStatus, searchValue, expeditionServices, expeditionRoutes, undefined, availabilityId);
    return this.getData(
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
      page,
    );
  }

  getData = (
    content: any,
    number_of_elements: any,
    total_elements: any,
    total_pages: any,
    first: any,
    last: any,
    page: any,
  ) => {
    const data = initialTripsState;
    data.value = objectToCamel(content) as Array<ITrip>;
    data.isFetchingTrips = false;
    data.pagination = {...initialPaginationState};
    data.pagination.numberOfElements = number_of_elements;
    data.pagination.totalPages = total_pages;
    data.pagination.totalElements = total_elements;
    data.pagination.actualPage = page;
    data.pagination.first = first;
    data.pagination.last = last;
    return data;
  };
}
